/* Copyright (c) 2020 IceRock MAG Inc. Use of this source code is governed by the Apache 2.0 license. */

import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useState,
} from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { HEALTH_STATUSES } from "../constants";
import styles from "./styles";
import classnames from 'classnames'; 

type IProps = {
  label: string;
  value: any;
  isEditing?: boolean;
  handler?: (val: any) => void;
  error?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  options?: Record<any, any>;
} & WithStyles<typeof styles> &
  Record<string, any>;

const OrderHealthStatus = withStyles(styles)(
  ({
    label,
    value,
    handler,
    error,
    isEditing,
    onClick,
    options,
    classes,
  }: IProps) => {
    const onChange = useCallback(
      (event) => {
        if (!handler) return;

        handler(event.target.value);
      },
      [value, handler]
    );

    const ref = useRef<HTMLLabelElement>(null);
    const [labelWidth, setLabelWidth] = useState(0);

    useEffect(() => {
      setLabelWidth((ref.current && ref.current.clientWidth) || 0);
    }, [ref.current]);

    const backgroundColor = useMemo(() => {
      const current = HEALTH_STATUSES.find((status) => status.id === value);
      return (current && current.bg) || "transparent";
    }, [value]);

    const color = useMemo(() => {
      const current = HEALTH_STATUSES.find((status) => status.id === value);
      return (current && current.color) || "#898E95";
    }, [value]);

    return isEditing ? (
      <FormControl variant="outlined">
        <InputLabel htmlFor={label} style={{ whiteSpace: "nowrap" }} ref={ref}>
          {label}
        </InputLabel>

        <Select
          variant="outlined"
          id={label}
          name={label}
          value={!value ? "" : value}
          onChange={onChange}
          error={!!error}
          inputProps={{ className: "select" }}
          labelWidth={labelWidth}
          style={{ minWidth: labelWidth + 40 }}
        >
          <MenuItem value="">...</MenuItem>

          {options &&
            Object.keys(options).map((item) => (
              <MenuItem key={item} value={item}>
                {options[item]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    ) : (
      <div onClick={onClick} className={classes.status}>
        <div
          className={classnames(classes.color, {
            [classes.empty]: !value || !options || !options[value],
          })}
          style={{ backgroundColor, color }}
        >
          {(options && options[value]) || <div>Not set</div>}
        </div>
      </div>
    );
  }
);

export { OrderHealthStatus };
